import { Component } from '@theme/component';
import { ReelPlayback, onScrollFrame, prefersReducedMotion, scrollProgress } from '@theme/ola-reel';

/**
 * Ola · Collage de marca.
 *
 * Mide dónde está el collage respecto a la pantalla y lo deja en `--p` (de -1, entrando por
 * abajo, a 1, saliendo por arriba) y `--pa` (su valor absoluto). El CSS mueve cada foto según su
 * profundidad y las acerca al reel cuando el collage está al centro. Con movimiento reducido no
 * hay movimiento. Solo mide mientras el collage está cerca de la pantalla.
 *
 * El reel se reproduce sin sonido solo mientras se ve; los botones los maneja `ReelPlayback`.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} composition - El collage (reel y fotos).
 * @property {HTMLButtonElement} [soundButton]
 * @property {HTMLButtonElement} [pauseButton]
 *
 * @extends {Component<Refs>}
 */
export class OlaCollage extends Component {
  requiredRefs = ['composition'];

  /** @type {AbortController | undefined} */
  #controller;
  /** @type {ReelPlayback | undefined} */
  #playback;
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  /** Pausa o reproduce el reel. */
  togglePlay() {
    this.#playback?.togglePlay();
  }

  /** Activa o quita el sonido del reel. */
  toggleSound() {
    this.#playback?.toggleSound();
  }

  #setup() {
    this.#teardown();

    const video = this.querySelector('video');
    if (video) {
      this.#playback = new ReelPlayback(video, { pause: this.refs.pauseButton, sound: this.refs.soundButton });
    }

    if (prefersReducedMotion()) return;

    // Solo escucha el scroll mientras el collage está cerca de la pantalla.
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.#controller?.abort();
        if (!entry?.isIntersecting) return;
        this.#controller = new AbortController();
        onScrollFrame(() => this.#update(), this.#controller.signal);
      },
      { rootMargin: '20% 0px' }
    );
    this.#observer.observe(this.refs.composition);
  }

  #teardown() {
    this.#observer?.disconnect();
    this.#controller?.abort();
    this.#playback?.destroy();
    this.#playback = undefined;
  }

  #update() {
    const progress = scrollProgress(this.refs.composition, 'pass');
    this.style.setProperty('--p', progress.toFixed(4));
    this.style.setProperty('--pa', Math.abs(progress).toFixed(4));
  }
}

if (!customElements.get('ola-collage')) {
  customElements.define('ola-collage', OlaCollage);
}
